import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import DocsLayout, { pagerFor } from 'Containers/MCP/docs/DocsLayout';
import { TOOL_CATEGORIES, TOOL_COUNT } from 'data/mcpTools';

import './reference.scss';

const PATH = '/mcp/docs/tools';
const { prev: PREV, next: NEXT } = pagerFor(PATH);

const hasDefault = (param) =>
  param.default !== null && param.default !== undefined;

const formatDefault = (value) => {
  if (typeof value === 'string') return value.length ? value : '""';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) return JSON.stringify(value);
  return String(value);
};

const ParamTable = ({ tool }) => {
  const showDefaults = tool.params.some(hasDefault);

  return (
    <>
      <p className="mcp-ref__params-title">Parameters</p>
      <div
        className="mcp-docs__table-wrap mcp-ref__table-wrap"
        tabIndex={0}
        role="group"
        aria-label={`Parameters for ${tool.name}`}
      >
        <table className="mcp-docs__table mcp-ref__table">
          <caption className="visually-hidden">
            {`Parameters accepted by the ${tool.name} tool`}
          </caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Type</th>
              <th scope="col">Required</th>
              <th scope="col">Description</th>
              {showDefaults ? <th scope="col">Default</th> : null}
            </tr>
          </thead>
          <tbody>
            {tool.params.map((param) => (
              <tr key={param.name}>
                <th scope="row">{param.name}</th>
                <td>
                  <span className="mcp-ref__type">{param.type}</span>
                </td>
                <td>
                  {param.required ? (
                    <span className="mcp-ref__req">Required</span>
                  ) : (
                    <span className="mcp-ref__opt">Optional</span>
                  )}
                </td>
                <td>{param.description}</td>
                {showDefaults ? (
                  <td>
                    {hasDefault(param) ? (
                      <code className="mcp-docs__code-inline">
                        {formatDefault(param.default)}
                      </code>
                    ) : (
                      <span className="mcp-ref__opt" aria-label="no default">
                        &mdash;
                      </span>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const ToolCard = ({ tool }) => (
  <article className="mcp-ref__tool">
    <h3 className="mcp-ref__tool-name" id={`tool-${tool.name}`}>
      {tool.name}
    </h3>
    {(tool.requiresConfirmation || tool.plan) && (
      <p className="mcp-ref__tool-badges">
        {tool.requiresConfirmation && (
          <span className="mcp-ref__badge mcp-ref__badge--confirm">
            Asks you to confirm
          </span>
        )}
        {tool.plan && (
          <span className="mcp-ref__badge mcp-ref__badge--plan">
            {`${tool.plan} plan`}
          </span>
        )}
      </p>
    )}
    <p className="mcp-ref__tool-desc">{tool.description}</p>
    {tool.params.length ? (
      <ParamTable tool={tool} />
    ) : (
      <p className="mcp-ref__no-params">No parameters.</p>
    )}
  </article>
);

const ToolReference = () => {
  const [query, setQuery] = useState('');
  const term = query.trim().toLowerCase();

  const categories = useMemo(() => {
    if (!term) return TOOL_CATEGORIES;

    return TOOL_CATEGORIES.map((category) => ({
      ...category,
      tools: category.tools.filter(
        (tool) =>
          tool.name.toLowerCase().includes(term) ||
          tool.description.toLowerCase().includes(term)
      ),
    })).filter((category) => category.tools.length > 0);
  }, [term]);

  const matchCount = useMemo(
    () =>
      categories.reduce((total, category) => total + category.tools.length, 0),
    [categories]
  );

  return (
    <DocsLayout
      eyebrow="Using it"
      title="Tool reference"
      lede={
        <>
          Tools are the actions the agent can take on your behalf &mdash;
          drafting a document, generating a brief, searching your content
          library, publishing a post. Every tool is scoped to the brand you have
          selected, so the same request produces different results depending on
          which brand is active.
        </>
      }
      stats={[
        { value: `${TOOL_COUNT}`, label: 'tools' },
        { value: `${TOOL_CATEGORIES.length}`, label: 'categories' },
        { value: 'Live', label: 'generated from the server' },
      ]}
      prev={PREV}
      next={NEXT}
    >
      <div className="mcp-ref">
        <div className="mcp-docs__callout mcp-docs__callout--tip">
          <p className="mcp-docs__callout-title">
            You do not call these tools yourself
          </p>
          <p>
            Ask Claude for what you want in plain language &mdash; &ldquo;draft
            a LinkedIn post about our new pricing&rdquo; &mdash; and it chooses
            the right tools itself. The names below are worth knowing when you
            want to be precise (&ldquo;use{' '}
            <code className="mcp-docs__code-inline">list_brands</code> first&rdquo;)
            or when you are working out why something did or did not happen.
          </p>
          <p>
            Read-only context the agent can pull in is listed separately under{' '}
            <Link to="/mcp/docs/resources">Resources</Link>.
          </p>
        </div>

        <div className="mcp-ref__filter">
          <label htmlFor="mcp-tool-filter">Filter tools</label>
          <input
            id="mcp-tool-filter"
            type="search"
            value={query}
            autoComplete="off"
            placeholder="Search by name or description…"
            aria-describedby="mcp-tool-filter-count"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setQuery('');
            }}
          />
          {query ? (
            <button
              type="button"
              className="mcp-ref__filter-clear"
              aria-label="Clear tool filter"
              onClick={() => setQuery('')}
            >
              &times;
            </button>
          ) : null}
        </div>

        <p
          className="mcp-ref__count"
          id="mcp-tool-filter-count"
          role="status"
          aria-live="polite"
        >
          {term
            ? `${matchCount} of ${TOOL_COUNT} tools match your search.`
            : `Showing all ${TOOL_COUNT} tools, grouped by category.`}
        </p>

        <nav aria-label="Jump to tool category">
          <ul className="mcp-ref__jump">
            {categories.map((category) => (
              <li key={category.id}>
                <a href={`#${category.id}`}>
                  {category.label} ({category.tools.length})
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {categories.length === 0 ? (
          <p className="mcp-ref__empty">
            No tools match that search. Try a shorter term, or{' '}
            <button
              type="button"
              className="mcp-ref__inline-btn"
              onClick={() => setQuery('')}
            >
              clear the filter
            </button>
            .
          </p>
        ) : (
          categories.map((category) => (
            <section
              key={category.id}
              id={category.id}
              className="mcp-ref__section"
              aria-labelledby={`${category.id}-heading`}
            >
              <h2 id={`${category.id}-heading`}>{category.label}</h2>
              <p className="mcp-ref__section-blurb">{category.blurb}</p>
              <div className="mcp-ref__tools">
                {category.tools.map((tool) => (
                  <ToolCard key={tool.name} tool={tool} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </DocsLayout>
  );
};

export default ToolReference;
